/*
 * Dev-only i18n affordances — Quality Gate dev tooling (UPT-172 / UPT-37 §3).
 *
 * Two cheap self-checks for Rules 1–3, available in development only:
 *
 *  1. Pseudo-locale (`?lang=pseudo`): wraps every translated string in brackets and
 *     pads it ~+40%, so (a) any hard-coded / un-keyed string is instantly visible
 *     (it is NOT bracketed) and (b) layouts that can't survive German/Finnish-length
 *     text break loudly in dev instead of silently in production.
 *
 *  2. RTL preview (`?dir=rtl`): forces document direction to rtl regardless of the
 *     active locale, so any physical-direction CSS that slipped past Rule 3a is
 *     visible without seeding an Arabic/Hebrew catalog.
 *
 * Both are also togglable via localStorage (persist across reloads) and surfaced by
 * the in-app <DevI18nToggle/> widget. ALL of this is guarded by import.meta.env.DEV
 * so it tree-shakes out of production bundles.
 */

export type DevDir = 'ltr' | 'rtl';

const LS_LOCALE = 'geode.dev.i18nLocale';
const LS_DIR = 'geode.dev.i18nDir';

/** Dev affordances are only ever active in a dev build. */
export const devAffordancesEnabled = (): boolean => {
  try {
    return Boolean(import.meta.env && import.meta.env.DEV);
  } catch {
    return false;
  }
};

const readParam = (name: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
};

const readStore = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStore = (key: string, value: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* private mode / disabled storage — ignore */
  }
};

/**
 * Is the pseudo-locale active? URL param wins (and is persisted) so a shared
 * `?lang=pseudo` link sticks for the session.
 */
export const isPseudoLocaleActive = (): boolean => {
  if (!devAffordancesEnabled()) return false;
  const param = readParam('lang');
  if (param === 'pseudo') {
    writeStore(LS_LOCALE, 'pseudo');
    return true;
  }
  if (param && param !== 'pseudo') {
    writeStore(LS_LOCALE, null);
    return false;
  }
  return readStore(LS_LOCALE) === 'pseudo';
};

/** Forced text direction for the RTL preview, or null when not overridden. */
export const getDevDirOverride = (): DevDir | null => {
  if (!devAffordancesEnabled()) return null;
  const param = readParam('dir');
  if (param === 'rtl' || param === 'ltr') {
    writeStore(LS_DIR, param);
    return param;
  }
  const stored = readStore(LS_DIR);
  return stored === 'rtl' || stored === 'ltr' ? stored : null;
};

export const setPseudoLocale = (on: boolean): void => writeStore(LS_LOCALE, on ? 'pseudo' : null);
export const setDevDir = (dir: DevDir | null): void => writeStore(LS_DIR, dir);

// Latin-1 accented stand-ins keep text readable while looking "foreign".
const ACCENT_MAP: Record<string, string> = {
  a: 'á', e: 'é', i: 'í', o: 'ó', u: 'ú', y: 'ý', n: 'ñ', c: 'ç',
  A: 'Á', E: 'É', I: 'Í', O: 'Ó', U: 'Ú', Y: 'Ý', N: 'Ñ', C: 'Ç',
};

const PAD = '···'; // "···" padding to approximate ~+40% growth

const accent = (text: string): string =>
  text.replace(/[a-zA-Z]/g, (ch) => ACCENT_MAP[ch] ?? ch);

/**
 * Pseudo-localize a single ICU message string. Text outside `{...}` placeholders is
 * accented and padded; placeholders / ICU argument blocks are left untouched so the
 * message still parses and formats. The whole string is bracketed so missing keys
 * (rendered as raw, un-bracketed ids) jump out.
 */
export const pseudoLocalizeMessage = (message: string): string => {
  if (!message) return message;

  // Complex ICU (plural/select/number/date: a `{arg, type, ...}` block) must not be
  // touched — accenting its keywords would make the message fail to parse. Bracket +
  // pad the whole thing so expansion is still visible, but leave the text intact.
  const hasComplexIcu = /\{\s*[\w.]+\s*,/.test(message);
  if (hasComplexIcu) {
    const padCount = Math.max(1, Math.round((message.length * 0.4) / PAD.length));
    return `⟦${message} ${PAD.repeat(padCount)}⟧`;
  }

  // Simple case: accent literal text, preserve `{placeholder}` substitutions.
  const parts = message.split(/(\{[^{}]*\})/g);
  const transformed = parts
    .map((part) => (part.startsWith('{') && part.endsWith('}') ? part : accent(part)))
    .join('');
  const visibleLen = transformed.replace(/\{[^{}]*\}/g, '').length;
  const padCount = Math.max(1, Math.round((visibleLen * 0.4) / PAD.length));
  return `⟦${transformed} ${PAD.repeat(padCount)}⟧`;
};

let cache: Record<string, string> | null = null;
let cacheSource: Record<string, string> | null = null;

/**
 * Build (and memoize) the pseudo catalog from the default-locale messages. Memoized
 * by source-object identity so repeated renders don't re-transform thousands of keys.
 */
export const buildPseudoMessages = (
  source: Record<string, string>,
): Record<string, string> => {
  if (cache && cacheSource === source) return cache;
  const out: Record<string, string> = {};
  for (const key of Object.keys(source)) {
    const value = source[key];
    out[key] = typeof value === 'string' ? pseudoLocalizeMessage(value) : value;
  }
  cache = out;
  cacheSource = source;
  return out;
};
